import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { Target, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

function fmtGBP(v, symbol = '£') {
  const n = Number(v || 0);
  return `${symbol}${Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function VarianceCell({ variance, pct }) {
  if (variance === 0 && (pct === null || pct === undefined)) {
    return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Minus size={11} /> —</span>;
  }
  const pos = variance >= 0;
  const Icon = pos ? ArrowUpRight : ArrowDownRight;
  const color = pos ? 'text-green-600' : 'text-red-600';
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${color} font-medium tabular-nums`}>
      <Icon size={11} />
      {pos ? '+' : '-'}{fmtGBP(Math.abs(variance))}
      {pct !== null && pct !== undefined && ` (${pos ? '+' : ''}${pct}%)`}
    </span>
  );
}

export default function TargetsVsActualsReport() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rRes, sRes] = await Promise.all([
        axios.get(`${API}/reports/targets-vs-actuals?months=${months}`),
        axios.get(`${API}/settings?lite=true`),
      ]);
      setData(rRes.data || []);
      setSettings(sRes.data || {});
    } catch (e) { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [months]);

  const symbol = settings.currency_symbol || '£';
  const mode = data.length > 0 ? data[0].mode : (settings.business_mode || 'single');
  const reversed = [...data].reverse();

  return (
    <div className="space-y-4" data-testid="targets-vs-actuals-report">
      <Card className="p-4 rounded-2xl border shadow-sm flex items-center gap-3 flex-wrap">
        <Target size={16} className="text-primary" />
        <span className="text-sm font-medium">Period:</span>
        <Select value={String(months)} onValueChange={v => setMonths(parseInt(v))}>
          <SelectTrigger className="w-40 rounded-xl" data-testid="tva-months-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="3">Last 3 months</SelectItem>
            <SelectItem value="6">Last 6 months</SelectItem>
            <SelectItem value="12">Last 12 months</SelectItem>
          </SelectContent>
        </Select>
        <Link to="/admin/targets" className="ml-auto">
          <Button variant="outline" size="sm" className="rounded-xl">Manage targets</Button>
        </Link>
      </Card>

      {loading && <p className="text-sm text-muted-foreground" data-testid="tva-loading">Loading...</p>}

      {!loading && reversed.map(row => (
        <Card key={row.month} className="rounded-2xl border shadow-sm overflow-hidden" data-testid={`tva-month-${row.month}`}>
          <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
            <h3 className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>{row.month}</h3>
            <div className="flex items-center gap-4 text-xs">
              <span className="text-muted-foreground">Target: <strong className="text-foreground tabular-nums">{fmtGBP(row.salon_target, symbol)}</strong></span>
              <span className="text-muted-foreground">Actual: <strong className="text-foreground tabular-nums">{fmtGBP(row.salon_actual, symbol)}</strong></span>
              <VarianceCell variance={row.salon_variance} pct={row.salon_variance_pct} />
            </div>
          </div>
          {mode === 'team' && row.staff && row.staff.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  <th className="text-left p-3 font-medium text-muted-foreground">Staff</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Target</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Actual</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Variance</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Bookings</th>
                </tr>
              </thead>
              <tbody>
                {row.staff.map(s => (
                  <tr key={s.staff_id} className="border-b border-border/50 last:border-b-0" data-testid={`tva-row-${row.month}-${s.staff_id}`}>
                    <td className="p-3 font-medium">{s.staff_name}</td>
                    <td className="p-3 text-right tabular-nums">{fmtGBP(s.target, symbol)}</td>
                    <td className="p-3 text-right tabular-nums">{fmtGBP(s.actual, symbol)}</td>
                    <td className="p-3 text-right"><VarianceCell variance={s.variance} pct={s.variance_pct} /></td>
                    <td className="p-3 text-right tabular-nums">{s.booking_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {mode === 'single' && (
            <div className="p-4 grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Bookings</p>
                <p className="text-lg font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{row.salon_booking_count || 0}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Achievement</p>
                <p className="text-lg font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>
                  {row.salon_target > 0 ? `${Math.round((row.salon_actual / row.salon_target) * 100)}%` : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Variance</p>
                <p className="text-lg font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>
                  <VarianceCell variance={row.salon_variance} pct={row.salon_variance_pct} />
                </p>
              </div>
            </div>
          )}
        </Card>
      ))}

      {!loading && reversed.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-12" data-testid="tva-empty">No data yet. Set monthly targets and add some completed bookings.</p>
      )}
    </div>
  );
}
